import React, { useState, useEffect, useCallback } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '../ui/card';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Textarea } from '../ui/textarea';
import { Save, Loader2, ChevronDown, ChevronUp } from 'lucide-react';
import { toast } from 'sonner';
import { contentAPI } from '../../services/api';

const RenewableServicesEditor = () => {
  const [services, setServices] = useState([]);
  const [saving, setSaving] = useState(false);
  const [expanded, setExpanded] = useState(null);

  const load = useCallback(async () => {
    try { const res = await contentAPI.get('renewable_services'); setServices(Array.isArray(res.data) ? res.data : []); }
    catch { toast.error('Failed to load'); }
  }, []);

  useEffect(() => { load(); }, [load]);
  const save = async () => {
    setSaving(true);
    try { await contentAPI.update('renewable_services', services); toast.success('Renewable services saved!'); }
    catch { toast.error('Failed to save'); }
    finally { setSaving(false); }
  };

  const update = (idx, field, value) => {
    const copy = [...services];
    copy[idx] = { ...copy[idx], [field]: value };
    setServices(copy);
  };

  const updateArray = (idx, field, value) => {
    update(idx, field, value.split('\n').filter(s => s.trim()));
  };

  return (
    <div className="space-y-4 max-w-4xl">
      <p className="text-sm text-gray-500">Edit each renewable energy consulting service. Click to expand.</p>
      {services.map((svc, idx) => (
        <Card key={svc.id || idx}>
          <CardHeader className="cursor-pointer" onClick={() => setExpanded(expanded === idx ? null : idx)}>
            <div className="flex items-center justify-between">
              <CardTitle className="text-lg">{svc.title}</CardTitle>
              {expanded === idx ? <ChevronUp className="w-5 h-5" /> : <ChevronDown className="w-5 h-5" />}
            </div>
          </CardHeader>
          {expanded === idx && (
            <CardContent className="space-y-4">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                <div>
                  <label className="text-sm font-medium text-gray-700 mb-1 block">Title</label>
                  <Input value={svc.title || ''} onChange={e => update(idx, 'title', e.target.value)} />
                </div>
                <div>
                  <label className="text-sm font-medium text-gray-700 mb-1 block">Slug</label>
                  <Input value={svc.slug || ''} onChange={e => update(idx, 'slug', e.target.value)} />
                </div>
                <div>
                  <label className="text-sm font-medium text-gray-700 mb-1 block">Icon</label>
                  <Input value={svc.icon || ''} onChange={e => update(idx, 'icon', e.target.value)} />
                </div>
                <div>
                  <label className="text-sm font-medium text-gray-700 mb-1 block">Image URL</label>
                  <Input value={svc.image || ''} onChange={e => update(idx, 'image', e.target.value)} />
                </div>
              </div>
              {svc.image && <img src={svc.image} alt="Preview" className="h-24 w-full object-cover rounded-lg" />}
              <div>
                <label className="text-sm font-medium text-gray-700 mb-1 block">Short Description</label>
                <Textarea rows={2} value={svc.description || ''} onChange={e => update(idx, 'description', e.target.value)} />
              </div>
              <div>
                <label className="text-sm font-medium text-gray-700 mb-1 block">Full Description</label>
                <Textarea rows={4} value={svc.fullDescription || ''} onChange={e => update(idx, 'fullDescription', e.target.value)} />
              </div>
              <div>
                <label className="text-sm font-medium text-gray-700 mb-1 block">Deliverables (one per line)</label>
                <Textarea rows={4} value={(svc.deliverables || []).join('\n')} onChange={e => updateArray(idx, 'deliverables', e.target.value)} />
              </div>
              <div>
                <label className="text-sm font-medium text-gray-700 mb-1 block">Process Steps (one per line)</label>
                <Textarea rows={4} value={(svc.process || []).join('\n')} onChange={e => updateArray(idx, 'process', e.target.value)} />
              </div>
              <div>
                <label className="text-sm font-medium text-gray-700 mb-1 block">Benefits</label>
                <Textarea rows={2} value={svc.benefits || ''} onChange={e => update(idx, 'benefits', e.target.value)} />
              </div>
            </CardContent>
          )}
        </Card>
      ))}
      <Button onClick={save} disabled={saving} className="w-full" data-testid="save-renewable">
        {saving ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Save className="w-4 h-4 mr-2" />} Save Renewable Services
      </Button>
    </div>
  );
};

export default RenewableServicesEditor;
